import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Download, Copy, Check, ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OpenAPIJsonPage() {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchSpec = async () => {
      try {
        const response = await fetch(`${API_URL}/api/openapi.json`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setSpec(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching OpenAPI spec:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSpec();
  }, []);

  const handleDownload = () => {
    if (!spec) return;
    const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'okreserva-openapi.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON descargado');
  };

  const handleCopy = () => {
    if (!spec) return;
    navigator.clipboard.writeText(JSON.stringify(spec, null, 2));
    setCopied(true);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white">Cargando OpenAPI Spec...</p>
          <p className="text-gray-400 text-sm mt-2">Conectando a {API_URL}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">Error al cargar OpenAPI</p>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <div className="space-y-2">
            <a href={`${API_URL}/api/openapi.json`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full gap-2">
                <ExternalLink className="h-4 w-4" />
                Abrir directamente
              </Button>
            </a>
            <Link to="/admin/api-docs">
              <Button variant="ghost" className="w-full gap-2 text-gray-400">
                <ArrowLeft className="h-4 w-4" />
                Volver a API Docs
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <header className="sticky top-0 z-10 bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/api-docs">
              <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold">OpenAPI Specification</h1>
              <p className="text-sm text-gray-400">OkReserva API v{spec?.info?.version || '2.0.0'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCopy} className="gap-2" disabled={!spec}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
            <Button onClick={handleDownload} className="gap-2 bg-orange-500 hover:bg-orange-600" disabled={!spec}>
              <Download className="h-4 w-4" />
              Descargar JSON
            </Button>
          </div>
        </div>
      </header>
      
      <main className="flex-1 max-w-7xl mx-auto p-4 w-full">
        <pre className="bg-gray-800 rounded-lg p-4 overflow-auto text-sm text-gray-300" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {spec ? JSON.stringify(spec, null, 2) : 'No data available'}
        </pre>
      </main>
    </div>
  );
}
